// src/pages/VerifyEmailPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axiosConfig';

type VerifyState = 'verifying' | 'success' | 'error';

type VerifyResponse = {
  success?: boolean;
  message?: string;
};

const inFlightTokens = new Map<string, Promise<VerifyResponse>>();

function requestVerification(token: string): Promise<VerifyResponse> {
  const existing = inFlightTokens.get(token);
  if (existing) return existing;

  const request = api
    .get<VerifyResponse>('/user/verify-email', {
      params: { token },
      timeout: 12_000,
    })
    .then(({ data }) => data)
    .finally(() => {
      // 같은 렌더 사이클/StrictMode 중복 호출만 합치고,
      // 이후 사용자의 명시적 재시도는 허용한다.
      window.setTimeout(() => inFlightTokens.delete(token), 1_000);
    });

  inFlightTokens.set(token, request);
  return request;
}

function resolveErrorMessage(error: unknown): string {
  const candidate = error as {
    response?: {
      data?: {
        message?: string;
        error?: string;
      };
    };
    message?: string;
  };

  const serverMessage =
    candidate?.response?.data?.message ||
    candidate?.response?.data?.error;

  if (serverMessage) return serverMessage;
  return '이메일 인증을 완료하지 못했습니다. 인증 링크를 다시 확인해 주세요.';
}

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(
    () => (searchParams.get('token') || '').trim(),
    [searchParams],
  );

  const [status, setStatus] = useState<VerifyState>('verifying');
  const [message, setMessage] = useState('이메일 인증을 확인하고 있습니다.');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      setStatus('error');
      setMessage('이메일 인증 토큰이 없습니다.');
      return undefined;
    }

    setStatus('verifying');
    setMessage('이메일 인증을 확인하고 있습니다.');

    requestVerification(token)
      .then((data) => {
        if (cancelled) return;

        if (data?.success === false) {
          setStatus('error');
          setMessage(data?.message || '이메일 인증을 완료하지 못했습니다.');
          return;
        }

        setStatus('success');
        setMessage(data?.message || '이메일 인증이 완료되었습니다. 로그인해 주세요.');
      })
      .catch((error) => {
        if (cancelled) return;
        setStatus('error');
        setMessage(resolveErrorMessage(error));
      });

    return () => {
      cancelled = true;
    };
  }, [token, retryKey]);

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 180px)',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 6,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 520,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: '1px solid rgba(120,140,180,.22)',
          bgcolor: '#0a1320',
          color: '#eaf2ff',
          textAlign: 'center',
        }}
      >
        {status === 'verifying' && (
          <>
            <CircularProgress size={34} />
            <Typography sx={{ mt: 2, fontSize: 20, fontWeight: 950 }}>
              이메일 인증 확인 중
            </Typography>
            <Typography sx={{ mt: 1, color: '#9fb4d8', fontSize: 13, lineHeight: 1.65 }}>
              잠시만 기다려 주세요.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
              {message}
            </Alert>
            <Typography sx={{ fontSize: 22, fontWeight: 950 }}>
              이메일 인증 완료
            </Typography>
            <Typography sx={{ mt: 1, color: '#9fb4d8', fontSize: 13, lineHeight: 1.65 }}>
              이제 가입한 이메일과 비밀번호로 로그인할 수 있습니다.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/', { replace: true })}
              sx={{ mt: 3, minHeight: 44, px: 3, fontWeight: 950 }}
            >
              로그인하러 가기
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
              {message}
            </Alert>
            <Typography sx={{ fontSize: 22, fontWeight: 950 }}>
              이메일 인증 확인 필요
            </Typography>
            <Typography sx={{ mt: 1, color: '#9fb4d8', fontSize: 13, lineHeight: 1.65 }}>
              링크가 만료됐거나 이미 사용된 링크일 수 있습니다.
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              {token && (
                <Button
                  variant="outlined"
                  onClick={() => setRetryKey((value) => value + 1)}
                  sx={{ minHeight: 42, fontWeight: 900 }}
                >
                  다시 확인
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => navigate('/', { replace: true })}
                sx={{ minHeight: 42, fontWeight: 950 }}
              >
                홈으로 이동
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default VerifyEmailPage;
